import React from 'react'
import { useMutation, useQuery } from '@apollo/client'
import type { ResultOf } from 'gql.tada'
import { format, parse } from 'date-fns'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Button, Label, TimePicker } from '@/components'
import { Select } from '@/components_deprecated'
import {
  COMPANY_WORK_INFRINGEMENT_SETTINGS_QUERY,
  PATCH_COMPANY_WORK_INFRINGEMENT_SETTINGS,
} from '@/modules/work-infringements/fats-settings-operations'
import { Module } from '@/modules/companies/consts'
import { ModuleResponsibleUsers } from '../components/ModuleResponsibleUsers'
import { InfoBox, Section, SettingsWrapper } from '../components/common'
import { SettingRow } from '../components/setting-row'

type CompanySettings = NonNullable<
  ResultOf<
    typeof COMPANY_WORK_INFRINGEMENT_SETTINGS_QUERY
  >['companyWorkInfringementSettings']
>

interface FATSParametersForm {
  generationEnabled: boolean
  referencePeriodWeeks: number
  maxDailyWorkHours: number
  nightRuleEnabled: boolean
  nightDailyLimitHours: number
  nightWindowStart: Date
  nightWindowEnd: Date
  maxWeeklyWorkHours: number
  maxAverageWeeklyWorkHours: number
  weeklyOrdinaryHours: number
  annualOvertimeLimitHours: number
}

// Backend Time scalar is serialized as HH:MM:SS; TimePicker works on Date.
const toPickerTime = (value: string) => parse(value, 'HH:mm:ss', new Date())
const toApiTime = (value: Date) => format(value, 'HH:mm:ss')

const rangeOptions = (
  from: number,
  count: number,
  label: (n: number) => string
) =>
  Array.from({ length: count }, (_, i) => ({
    label: label(from + i),
    value: from + i,
  }))

export const FATSParametersSettings: React.FC = () => {
  const { data, loading } = useQuery(COMPANY_WORK_INFRINGEMENT_SETTINGS_QUERY)
  const settings = data?.companyWorkInfringementSettings
  if (loading || !settings) return null
  return <FATSParametersForm settings={settings} />
}

const FATSParametersForm: React.FC<{ settings: CompanySettings }> = ({
  settings,
}) => {
  const t = useTranslate({
    title: 'settings.fats-parameters.title',
    info: 'settings.fats-parameters.info',
    generation: 'settings.fats-parameters.generation',
    generationInfo: 'settings.fats-parameters.generation-info',
    periodLength: 'settings.fats.period-length',
    maxDailyWorkLimit: 'settings.fats.max-daily-work-limit',
    nightRule: 'settings.fats-parameters.night-rule',
    nightDailyLimit: 'settings.fats-parameters.night-daily-limit',
    nightWindowStart: 'settings.fats-parameters.night-window-start',
    nightWindowEnd: 'settings.fats-parameters.night-window-end',
    maxWeekly: 'settings.fats-parameters.max-weekly',
    avgWeekly: 'settings.fats-parameters.avg-weekly',
    weeklyOrdinary: 'settings.fats-parameters.weekly-ordinary',
    annualOvertime: 'settings.fats-parameters.annual-overtime',
    nWeeks: ['settings.fats.n-weeks', { n: 0 }],
    nHours: ['common.n-hours', { n: 0 }],
    save: 'common.save',
  })

  const [patchSettings, { loading: saving }] = useMutation(
    PATCH_COMPANY_WORK_INFRINGEMENT_SETTINGS
  )

  const {
    formValues,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm<FATSParametersForm>({
    values: {
      generationEnabled: settings.generationEnabled,
      referencePeriodWeeks: settings.referencePeriodWeeks,
      maxDailyWorkHours: settings.maxDailyWorkHours,
      nightRuleEnabled: settings.nightRuleEnabled,
      nightDailyLimitHours: settings.nightDailyLimitHours,
      nightWindowStart: toPickerTime(settings.nightWindowStart),
      nightWindowEnd: toPickerTime(settings.nightWindowEnd),
      maxWeeklyWorkHours: settings.maxWeeklyWorkHours,
      maxAverageWeeklyWorkHours: settings.maxAverageWeeklyWorkHours,
      weeklyOrdinaryHours: settings.weeklyOrdinaryHours,
      annualOvertimeLimitHours: settings.annualOvertimeLimitHours,
    },
  })

  const periodLengthOptions = rangeOptions(16, 11, n => t.nWeeks({ n }))
  const maxDailyOptions = rangeOptions(13, 4, n => t.nHours({ n }))
  const nightDailyOptions = rangeOptions(8, 6, n => t.nHours({ n }))
  const maxWeeklyOptions = rangeOptions(48, 19, n => t.nHours({ n }))
  const avgWeeklyOptions = rangeOptions(40, 15, n => t.nHours({ n }))
  const weeklyOrdinaryOptions = rangeOptions(35, 14, n => t.nHours({ n }))
  const annualOvertimeOptions = [200, 250, 300, 350, 400].map(n => ({
    label: t.nHours({ n }),
    value: n,
  }))
  async function handleSave(values: FATSParametersForm) {
    await patchSettings({
      variables: {
        generationEnabled: values.generationEnabled,
        referencePeriodWeeks: values.referencePeriodWeeks,
        maxDailyWorkHours: values.maxDailyWorkHours,
        nightRuleEnabled: values.nightRuleEnabled,
        nightDailyLimitHours: values.nightDailyLimitHours,
        nightWindowStart: toApiTime(values.nightWindowStart),
        nightWindowEnd: toApiTime(values.nightWindowEnd),
        maxWeeklyWorkHours: values.maxWeeklyWorkHours,
        maxAverageWeeklyWorkHours: values.maxAverageWeeklyWorkHours,
        weeklyOrdinaryHours: values.weeklyOrdinaryHours,
        annualOvertimeLimitHours: values.annualOvertimeLimitHours,
      },
    })
  }

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox>
        <p>{t.info}</p>
      </InfoBox>
      <ModuleResponsibleUsers module={Module.WORK_DAY_INFRINGEMENTS} />
      <Section header={t.title}>
        <form
          className="space-y-8"
          onSubmit={event => {
            event.preventDefault()
            submit(handleSave)()
          }}
        >
          <SettingRow
            id="fats-generation"
            label={t.generation}
            description={t.generationInfo}
            checked={formValues.generationEnabled}
            onCheckedChange={handler('generationEnabled')}
          />

          <div className="grid items-end gap-x-6 gap-y-5 border-t border-stone-200 pt-6 sm:grid-cols-2">
            <SelectField
              label={t.periodLength}
              options={periodLengthOptions}
              value={formValues.referencePeriodWeeks}
              onChange={handler('referencePeriodWeeks')}
            />
            <SelectField
              label={t.maxDailyWorkLimit}
              options={maxDailyOptions}
              value={formValues.maxDailyWorkHours}
              onChange={handler('maxDailyWorkHours')}
            />
            <SelectField
              label={t.maxWeekly}
              options={maxWeeklyOptions}
              value={formValues.maxWeeklyWorkHours}
              onChange={handler('maxWeeklyWorkHours')}
            />
            <SelectField
              label={t.avgWeekly}
              options={avgWeeklyOptions}
              value={formValues.maxAverageWeeklyWorkHours}
              onChange={handler('maxAverageWeeklyWorkHours')}
            />
            <SelectField
              label={t.weeklyOrdinary}
              options={weeklyOrdinaryOptions}
              value={formValues.weeklyOrdinaryHours}
              onChange={handler('weeklyOrdinaryHours')}
            />
            <SelectField
              label={t.annualOvertime}
              options={annualOvertimeOptions}
              value={formValues.annualOvertimeLimitHours}
              onChange={handler('annualOvertimeLimitHours')}
            />
          </div>

          <div className="space-y-5 border-t border-stone-200 pt-6">
            <SettingRow
              id="fats-night-rule"
              label={t.nightRule}
              checked={formValues.nightRuleEnabled}
              onCheckedChange={handler('nightRuleEnabled')}
            />
            {formValues.nightRuleEnabled && (
              <div className="grid items-end gap-x-6 gap-y-5 sm:grid-cols-3">
                <SelectField
                  label={t.nightDailyLimit}
                  options={nightDailyOptions}
                  value={formValues.nightDailyLimitHours}
                  onChange={handler('nightDailyLimitHours')}
                />
                <div className="space-y-2">
                  <Label className="leading-snug">{t.nightWindowStart}</Label>
                  <TimePicker
                    value={formValues.nightWindowStart}
                    onChange={handler('nightWindowStart')}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="leading-snug">{t.nightWindowEnd}</Label>
                  <TimePicker
                    value={formValues.nightWindowEnd}
                    onChange={handler('nightWindowEnd')}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end border-t border-stone-200 pt-6">
            <Button type="submit" disabled={saving}>
              {t.save}
            </Button>
          </div>
        </form>
      </Section>
    </SettingsWrapper>
  )
}

const SelectField: React.FC<{
  label: string
  options: { label: string; value: number }[]
  value: number
  onChange: (value: number) => void
}> = ({ label, options, value, onChange }) => (
  <div className="space-y-2">
    <Label className="leading-snug">{label}</Label>
    <Select
      options={options}
      value={value}
      onChange={v => onChange(v ?? value)}
      nullable={false}
      fullWidth
    />
  </div>
)
